import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const EMPLOYEE_OPTIONS = [
  '0 – 5',
  '6 – 10',
  '11 – 50',
  '51 – 100',
  '101 – 250',
  '250 – 500',
  '500 – 1000',
  'Más de 1000',
];

interface FormState {
  full_name: string;
  email: string;
  phone: string;
  company_name: string;
  position: string;
  employee_count: string;
  looking_for: string;
}

const EMPTY: FormState = {
  full_name: '',
  email: '',
  phone: '',
  company_name: '',
  position: '',
  employee_count: '',
  looking_for: '',
};

async function getFunctionErrorMessage(error: any) {
  const ctx = error?.context;
  if (ctx && typeof ctx.clone === 'function') {
    try {
      const parsed = await ctx.clone().json();
      if (parsed?.error || parsed?.message) return parsed.error || parsed.message;
    } catch {
      try {
        const text = await ctx.clone().text();
        if (text) return text;
      } catch { /* noop */ }
    }
  }
  return error?.message ?? 'Intenta de nuevo más tarde.';
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ContactSalesDialog({ open, onOpenChange }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (field: keyof FormState) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleClose = (value: boolean) => {
    if (!value) {
      setForm(EMPTY);
      setSent(false);
    }
    onOpenChange(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.employee_count) {
      toast({ title: 'Selecciona el número de empleados', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('submit-sales-lead', {
        body: {
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          company_name: form.company_name.trim(),
          position: form.position.trim(),
          employee_count: form.employee_count,
          looking_for: form.looking_for.trim(),
        },
      });
      if (error) throw error;
      setSent(true);
    } catch (err: any) {
      toast({
        title: 'Error al enviar',
        description: await getFunctionErrorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {sent ? (
          <div className="flex flex-col items-center text-center py-8 gap-4">
            <CheckCircle2 className="w-14 h-14 text-primary" />
            <DialogTitle className="text-2xl">¡Mensaje recibido!</DialogTitle>
            <p className="text-muted-foreground">
              Gracias por tu interés en Líder IA. Nuestro equipo se pondrá en contacto contigo
              pronto para agendar una demo personalizada.
            </p>
            <Button onClick={() => handleClose(false)} className="mt-2">
              Cerrar
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Contactar al equipo de ventas</DialogTitle>
              <DialogDescription>
                Cuéntanos sobre tu empresa y nos pondremos en contacto para agendar una demo.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="sl-name">Nombre completo *</Label>
                  <Input
                    id="sl-name"
                    required
                    placeholder="María García"
                    value={form.full_name}
                    onChange={(e) => set('full_name')(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sl-email">Correo electrónico *</Label>
                  <Input
                    id="sl-email"
                    type="email"
                    required
                    placeholder="maria@empresa.com"
                    value={form.email}
                    onChange={(e) => set('email')(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="sl-phone">Celular *</Label>
                  <Input
                    id="sl-phone"
                    required
                    placeholder="+57 310 000 0000"
                    value={form.phone}
                    onChange={(e) => set('phone')(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sl-company">Nombre de la empresa *</Label>
                  <Input
                    id="sl-company"
                    required
                    placeholder="Innovatech S.A.S"
                    value={form.company_name}
                    onChange={(e) => set('company_name')(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="sl-position">Cargo *</Label>
                  <Input
                    id="sl-position"
                    required
                    placeholder="Director de Transformación Digital"
                    value={form.position}
                    onChange={(e) => set('position')(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Número de empleados *</Label>
                  <Select value={form.employee_count} onValueChange={set('employee_count')}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un rango" />
                    </SelectTrigger>
                    <SelectContent>
                      {EMPLOYEE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="sl-looking">¿Qué estás buscando? *</Label>
                <Textarea
                  id="sl-looking"
                  required
                  rows={4}
                  placeholder="Cuéntanos qué problema quieres resolver o qué solución estás buscando..."
                  value={form.looking_for}
                  onChange={(e) => set('looking_for')(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Enviar mensaje
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
